import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AppHttpClientApi } from '@helpers/api/http-client-api';
import { catchError, map, Observable, throwError } from 'rxjs';
import { APP_CONFIG, AppConfig } from '@cfg/env/app.config';
import { AppError } from '@domain-utils/app-error';
import { ApiException } from '@helpers/errors/api-error';
import { AppErrorItem } from '@domain-utils/app-error-item';
import {
  createMockUploadSuccessResponse,
  MOCK_FILE_UPLOAD_LOAD_ID
} from '@helpers/api/mocks/file-upload.mock';
import { SERVICE_ERROR_CONSTANTS } from './constants/service-error.const';
import {
  LoadRequest,
  LoadResponse,
  LoadStatus,
  UploadFileResponse
} from './models/load.model';

interface UploadErrorBody {
  error?: string;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  private readonly http = inject(AppHttpClientApi);
  private readonly appConfig: AppConfig = inject(APP_CONFIG);
  private readonly uploadResource = 'cargamasivaemp/bulk-loads/:loadId/file';
  private readonly initLoadResource = 'cargamasivaemp/bulk-loads/init';

  /**
   * Obtiene un nuevo identificador de carga desde el backend.
   * @returns Observable con el identificador generado.
   */
  generateLoadId(params: LoadRequest): Observable<string> {
    if (this.appConfig.useMocks) {
      return new Observable((observer) => {
        observer.next(MOCK_FILE_UPLOAD_LOAD_ID);
        observer.complete();
      });
    }

    return this.http
      .postPlain<LoadResponse>(this.initLoadResource, params)
      .pipe(
        map((response) => {
          const loadId = response?.loadId?.trim() ?? '';
          if (!loadId) {
            throw new AppError('DOCUMENT_ID_EMPTY', [
              {
                key: 'DOCUMENT_ID_NOT_FOUND',
                message:
                  'El servicio no retornó un identificador de carga válido.'
              }
            ]);
          }
          return loadId;
        }),
        catchError((error: unknown) => {
          if (error instanceof AppError) {
            return throwError(() => error);
          }
          if (error instanceof ApiException) {
            const codes: AppErrorItem[] = error.error.map((item) => ({
              key: item.key,
              message: item.message
            }));

            return throwError(
              () => new AppError('DOCUMENT_ID_GENERATION_FAILED', codes)
            );
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Sube un archivo al servidor utilizando un POST sencillo contra el endpoint indicado.
   */
  uploadFile(
    file: File | null,
    loadId: string
  ): Observable<UploadFileResponse> {
    if (this.appConfig.useMocks) {
      return new Observable((observer) => {
        observer.next(createMockUploadSuccessResponse(loadId));
        observer.complete();
      });
    }

    if (!(file instanceof File)) {
      return throwError(
        () =>
          new AppError(SERVICE_ERROR_CONSTANTS.UPLOAD.FAILED, [
            SERVICE_ERROR_CONSTANTS.UPLOAD.FILE_INVALID
          ])
      );
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    const uploadUrl = this.uploadResource.replace(':loadId', loadId);

    return this.http.postPlain<UploadFileResponse>(uploadUrl, formData).pipe(
      map((response) => this.normalizeUploadResponse(response, loadId)),
      catchError((error: unknown) => {
        if (error instanceof AppError) {
          return throwError(() => error);
        }
        if (error instanceof ApiException) {
          const codes: AppErrorItem[] = error.error.map((item) => ({
            key: item.key,
            message: item.message
          }));

          return throwError(
            () => new AppError(SERVICE_ERROR_CONSTANTS.UPLOAD.FAILED, codes)
          );
        }
        if (error instanceof HttpErrorResponse) {
          const body = error.error as UploadErrorBody | null;
          const message =
            body?.message ??
            SERVICE_ERROR_CONSTANTS.UPLOAD.UNKNOWN_ERROR.message;
          const codes: AppErrorItem[] = [
            { key: SERVICE_ERROR_CONSTANTS.UPLOAD.FAILED, message }
          ];
          return throwError(
            () => new AppError(SERVICE_ERROR_CONSTANTS.UPLOAD.FAILED, codes)
          );
        }

        return throwError(
          () =>
            new AppError(SERVICE_ERROR_CONSTANTS.UPLOAD.FAILED, [
              {
                key: SERVICE_ERROR_CONSTANTS.UPLOAD.UNKNOWN_ERROR.key,
                message:
                  error instanceof Error
                    ? error.message
                    : SERVICE_ERROR_CONSTANTS.UPLOAD.UNKNOWN_ERROR.message
              }
            ])
        );
      })
    );
  }

  private normalizeUploadResponse(
    response: UploadFileResponse | null | undefined,
    loadId: string
  ): UploadFileResponse {
    return {
      loadId: response?.loadId?.trim() ?? loadId,
      status: response?.status ?? LoadStatus.COMPLETED,
      statusDescription: response?.statusDescription ?? '',
      totalRows: response?.totalRows ?? 0,
      rowsWithErrors: response?.rowsWithErrors ?? 0,
      errorCount: response?.errorCount ?? 0,
      annotatedFileName: response?.annotatedFileName ?? null,
      annotatedFileBase64: response?.annotatedFileBase64 ?? null,
      hasErrors: response?.hasErrors ?? false
    };
  }
}
